import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import dayjs from 'dayjs'
import React from 'react'

import {
  fetchFamilyDashboard,
  searchApartmentParents,
  setParentBuilding
} from 'api/apartment'
import { fetchInteractionHistory, fetchTickets } from 'api/communication'
import { AutoComplete } from 'app/components/generic/FormFields'
import { Loading } from 'app/components/generic/LottieComponents'
import Slider from 'app/components/generic/Slider/slider'
// The main Parent Dashboard's own card chrome, not a copy of it: `Wrapper` is
// the 264x226 card, `LongWrapper` the full-width one, and Header/CardName/
// Heading render the uppercase letter-spaced title. `TitleDescription` is the
// label-over-value pair every card is built from.
import * as dash from 'app/components/shared/dashboardPageStyles'
import * as sum from 'app/components/shared/dashboardSummaryStyles'
import { TICKET_STATUS_OPTIONS } from 'app/constants'
import { NotFoundBlock } from 'app/pages/Dashboard/components/NotFoundBlock'
import { TitleDescription } from 'app/pages/Dashboard/components/TitleDescription'
// The membership and family cards are their own styled components on the main
// dashboard, not SummaryBlock wrappers. Reuse those so the two cards are the
// same object, not a lookalike.
import * as fam from 'app/pages/Dashboard/ParentDashboard/components/FamilyBlock/styles'
import * as mem from 'app/pages/Dashboard/ParentDashboard/components/MembershipBlock/styles'
import * as enrolStyles from 'app/pages/Dashboard/ParentDashboard/components/SummaryBlock/components/Enrolments/styles'
import { Interactions } from 'app/pages/Dashboard/ParentDashboard/components/SummaryBlock/components/Interactions'
import * as healthStyles from 'app/pages/Dashboard/ParentDashboard/components/SummaryBlock/components/LifeTimeHealth/styles'
import * as ticketStyles from 'app/pages/Dashboard/ParentDashboard/components/SummaryBlock/components/TicketsLog/styles'
// Read + log in one surface, the same drawer Revenue Goals opens. Reused
// rather than rebuilt so an interaction logged here is identical to one
// logged anywhere else — same CRM endpoints, same fields, same validation.
import { getIcon } from 'app/pages/Dashboard/ParentDashboard/helpers'
import InteractionDrawer from 'app/pages/RevenueGoals/InteractionDrawer'
// The dashboard's own icon map, keyed by the same card-type strings.
import { formatAmount } from 'app/utils'
import OhIcon from 'assets/images/oh_logo_transparent.svg'

import { ChildHistoryDrawer } from './ChildHistoryDrawer'

import { useApartments } from '../shared/apartmentStore'

const useStyles = makeStyles((theme) => ({
  picker: { width: '40%', minWidth: 260 },
  clickable: { cursor: 'pointer' },
  panel: { padding: theme.spacing(3) },
  tableWrap: { width: '100%', overflowX: 'auto' },
  muted: { color: theme.palette.text.secondary },
  empty: { padding: theme.spacing(4, 0), textAlign: 'center' }
}))

// The main card renders 'NA' for a session it cannot find.
const fmtSession = (iso) => (iso ? dayjs(iso).format('D MMM YY, h:mm A') : 'NA')

// The shared `Interactions` card reads `data[0].latest_interaction_*`, the shape
// the main dashboard's `getInteractionsData` builds. Handing it the raw API page
// (`{count, results}`) made it read "No interactions" for EVERY family even
// though the logs were saved — so coordinators logged the same call again and
// again. Newest interaction date first; the sort is stable, so same-day rows
// keep the API's own order (newest logged first).
const toInteractionCard = (page) => {
  const rows = [...(page?.results || [])].sort(
    (a, b) => new Date(b?.interaction_date) - new Date(a?.interaction_date)
  )
  if (!rows.length) return { data: [] }
  const latest = rows[0]
  return {
    data: [
      {
        latest_interaction_date: latest.interaction_date,
        latest_interaction_summary: latest.interaction_topics,
        latest_follow_up_date: latest.follow_up_date
      }
    ]
  }
}

// The card shell every block on the main dashboard uses, icon and all. `title`
// doubles as the key into `getIcon`, exactly as `summaryData.type` does there.
const Block = ({ title, iconType, children }) => (
  <sum.Wrapper>
    <sum.Header>
      <sum.CardName>
        {getIcon(iconType || title)}
        &nbsp;&nbsp;
        <sum.Heading>{title}</sum.Heading>
      </sum.CardName>
    </sum.Header>
    <sum.Body>{children}</sum.Body>
  </sum.Wrapper>
)

const WideBlock = ({ title, children }) => (
  <sum.LongWrapper>
    <sum.Header>
      <sum.CardName>
        {getIcon(title)}
        &nbsp;&nbsp;
        <sum.Heading>{title}</sum.Heading>
      </sum.CardName>
    </sum.Header>
    <sum.Body>{children}</sum.Body>
  </sum.LongWrapper>
)

// The membership card, rebuilt on MembershipBlock's own styles. An apartment
// parent has no centre and no CRM flags; their building is the equivalent, and
// the flat number sits alongside it.
const MembershipCard = ({ parent, onEditBuilding }) => (
  <mem.Wrapper width={264}>
    <mem.Body>
      <mem.InfoBlock>
        <TitleDescription heading="name" description={parent.name} />
        {/* Rendered even with no building set — this used to disappear
            entirely, so a parent who had enquired but never booked showed no
            building AND no way to give them one. `create_booking` was the only
            code path that ever set it. */}
        <TitleDescription
          heading="apartment"
          description={
            <mem.Heading>
              {parent.apartment ? (
                <>
                  <mem.Icon src={OhIcon} alt="oh-icon" />
                  {parent.apartment.toLowerCase()}
                </>
              ) : (
                <span>not set</span>
              )}
              {onEditBuilding ? (
                <Button size="small" color="primary" onClick={onEditBuilding}>
                  edit
                </Button>
              ) : null}
            </mem.Heading>
          }
        />
      </mem.InfoBlock>

      <mem.InfoBlock>
        <TitleDescription
          heading="flat no."
          description={parent.flat || 'NA'}
        />
        <TitleDescription heading="phone" description={parent.primaryPhone} />
      </mem.InfoBlock>

      <mem.InfoBlock>
        <TitleDescription
          heading="alt phone"
          description={parent.secondaryPhone || 'NA'}
        />
        <TitleDescription
          heading="heard via"
          description={parent.referralSource || 'NA'}
        />
      </mem.InfoBlock>
    </mem.Body>
  </mem.Wrapper>
)

// The family card, rebuilt on FamilyBlock's own styles. Each child is a button
// that opens their history drawer, exactly as on the main dashboard. Apartment
// parents have no guardians on file, so that section does not appear.
// `childRows`, not `children` — that name is React's own prop.
const FamilyCard = ({ childRows, onChildClick }) => (
  <fam.Wrapper width={264}>
    <fam.CardTitle>Family</fam.CardTitle>
    {childRows.length === 0 ? (
      <fam.Empty>No children on file.</fam.Empty>
    ) : (
      <>
        <fam.SectionLabel>Children</fam.SectionLabel>
        {childRows.map((c) => (
          <fam.ChildRow
            key={c.childId}
            as="button"
            type="button"
            onClick={() => onChildClick(c)}
            title={c.name}
            style={{
              width: '100%',
              background: 'transparent',
              border: 'none',
              textAlign: 'left'
            }}
          >
            <fam.Name>{c.name}</fam.Name>
            <fam.Role>{c.grade || c.ageGroup || '—'}</fam.Role>
          </fam.ChildRow>
        ))}
      </>
    )}
  </fam.Wrapper>
)

const DataTable = ({ classes, columns, rows, empty }) =>
  rows.length === 0 ? (
    <NotFoundBlock message={empty} />
  ) : (
    <TableContainer className={classes.tableWrap}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableCell key={c.label}>{c.label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            // Dashboard rows carry no stable id of their own.
            // eslint-disable-next-line react/no-array-index-key
            <TableRow key={i}>
              {columns.map((c) => (
                <TableCell key={c.label}>{c.render(r)}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )

// The drawer body. It takes `userInfo` and deliberately does not forward it to
// the DOM — Slider's SLIDER_OPENER handler reads
// `childComponent.props.userInfo[key]`, so the prop must exist on the element,
// but a <div> would warn about an unknown attribute.
//
// The main dashboard opens a `History` widget here. That reads
// `decodeJWT(getAccessToken()).sub` on mount and throws where there is no
// token, so these panes render the same table without that dependency.
// eslint-disable-next-line no-unused-vars
const Panel = ({ classes, userInfo, children }) => (
  <div className={classes.panel}>{children}</div>
)

const SliderBlock = ({ classes, type, userInfo, card, panel }) => (
  <Slider
    width="50%"
    blockType={`apartment-family-${type}`}
    placeholder={<div className={classes.clickable}>{card}</div>}
    childComponent={
      <Panel classes={classes} userInfo={userInfo || {}}>
        {panel}
      </Panel>
    }
  />
)

const ApartmentFamilyDashboard = () => {
  const classes = useStyles()
  const [parentId, setParentId] = React.useState('')
  const [phone, setPhone] = React.useState('')
  const [data, setData] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [tickets, setTickets] = React.useState([])
  const [openChild, setOpenChild] = React.useState(null)
  const [interactions, setInteractions] = React.useState(null)
  const [logOpen, setLogOpen] = React.useState(false)
  const apartments = useApartments()
  const [buildingEdit, setBuildingEdit] = React.useState(null)
  const [savingBuilding, setSavingBuilding] = React.useState(false)

  // Deliberately unscoped: a family is looked up by who they are, not by which
  // building you happened to have selected. Passing no apartment id searches
  // every parent in Alfred. `family-dashboard/` is apartment-agnostic too — it
  // resolves the Parent by pk with no building filter — so any result here
  // loads. A parent with no apartment renders with empty enrolment/payment
  // cards, which is the honest answer for someone who has never been in one.
  const fetchParents = React.useCallback(
    (text) => searchApartmentParents('', text),
    []
  )

  React.useEffect(() => {
    if (!parentId) {
      setData(null)
      return undefined
    }
    let active = true
    setLoading(true)
    fetchFamilyDashboard(parentId)
      .then((d) => active && setData(d))
      .catch(() => active && setData(null))
      // `finally` matters: without it a failed fetch spins forever.
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [parentId])

  // Tickets are main-system data for every parent, keyed on phone number.
  React.useEffect(() => {
    if (!phone) {
      setTickets([])
      return undefined
    }
    let active = true
    fetchTickets({ queries: { user_phone_number: phone, limit: 500 } })
      .then((r) => active && setTickets(r?.results || []))
      .catch(() => active && setTickets([]))
    return () => {
      active = false
    }
  }, [phone])

  // Interactions are main-system CRM rows, keyed on the Parent uuid (tickets
  // above are keyed on phone — different endpoints, different keys).
  const loadInteractions = React.useCallback(() => {
    if (!parentId) {
      setInteractions(null)
      return
    }
    fetchInteractionHistory({ queries: { parent: parentId, limit: 100 } })
      .then((r) => setInteractions(toInteractionCard(r)))
      .catch(() => setInteractions(null))
  }, [parentId])

  React.useEffect(loadInteractions, [loadInteractions])

  const saveBuilding = async () => {
    setSavingBuilding(true)
    try {
      await setParentBuilding(parentId, {
        apartment: buildingEdit.apartment,
        flat: buildingEdit.flat
      })
      setBuildingEdit(null)
      // Re-read rather than patching local state: the building drives the
      // enrolment, payment and attendance cards, not just this one line.
      const fresh = await fetchFamilyDashboard(parentId)
      setData(fresh)
    } finally {
      setSavingBuilding(false)
    }
  }

  const onSelectParent = (opt) => {
    setParentId(opt?.uuid || '')
    setPhone(opt?.phone || '')
  }

  const header = (
    <dash.Header>
      <FormControl className={classes.picker}>
        <AutoComplete
          // Opt-ins this screen makes: 300ms throttle instead of the shared
          // 5000ms default (five seconds of an empty box reads as broken),
          // and fetch-on-open so clicking shows options instead of an empty
          // menu. Both default to the OLD behaviour everywhere else in Alfred.
          throttleMs={300}
          fetchOnOpen
          async
          name="familyParent"
          label="Parent (search by name or number)"
          multiple={false}
          // Browsing still works on an empty box (`handleSearch` gates on
          // `value.length >= minCharactersToSearch`, false forever when that
          // prop is undefined), but nothing is auto-selected any more: with no
          // building to scope to, "the first parent" is an arbitrary row out of
          // every parent in Alfred. The screen opens on its own empty state
          // until someone actually searches.
          searchWithEmptyValue
          minCharactersToSearch={0}
          fetchValues={fetchParents}
          onChange={(e, opt) => onSelectParent(opt)}
          size="small"
        />
      </FormControl>
    </dash.Header>
  )

  if (!parentId || loading || !data) {
    return (
      <dash.Container>
        {header}
        {loading ? (
          <Loading />
        ) : (
          <Typography className={`${classes.muted} ${classes.empty}`}>
            {parentId
              ? 'Could not load this family.'
              : 'Search for a parent to see their dashboard.'}
          </Typography>
        )}
      </dash.Container>
    )
  }

  const { parent, children, enrolments, payments, paymentSummary } = data
  const attendance = data.attendance || {}
  const userInfo = { uuid: parentId, phone_number: phone }
  // `TICKET_STATUS_OPTIONS.RESOLVED` is 'Resolved' — comparing to a lowercase
  // literal would count every ticket as open.
  const openTickets = tickets.filter(
    (t) => t.ticket_status !== TICKET_STATUS_OPTIONS.RESOLVED
  )
  const activeEnrolments = data.stats?.activeEnrolments ?? 0
  const upcoming = enrolments.filter((e) => e.status === 'upcoming').length
  const monthly = attendance.monthly || {}
  const lifetime = attendance.lifetime || {}
  const upcomingSession = attendance.upcomingSession

  const enrolmentColumns = [
    { label: 'Start Date', render: (r) => r.startDate || 'NA' },
    { label: 'Subject', render: (r) => r.program },
    { label: 'Batch timing', render: (r) => r.schedule || 'NA' },
    { label: 'Kid', render: (r) => r.child },
    { label: 'Freq', render: (r) => r.frequency },
    { label: 'Status', render: (r) => <Chip size="small" label={r.status} /> },
    { label: 'End date', render: (r) => r.endDate || 'NA' }
  ]

  const paymentColumns = [
    { label: 'Month', render: (r) => r.billingMonth },
    { label: 'Gross', render: (r) => formatAmount(r.gross) },
    { label: 'Net', render: (r) => formatAmount(r.net) },
    { label: 'Status', render: (r) => <Chip size="small" label={r.status} /> },
    { label: 'Paid at', render: (r) => r.paidAt || 'NA' }
  ]

  // The same fields the main dashboard's tickets-log table reads.
  const ticketColumns = [
    { label: 'Reported Date', render: (r) => r.reported_date || 'NA' },
    {
      label: 'Ticket Status',
      render: (r) => <Chip size="small" label={r.ticket_status} />
    },
    { label: 'Ticket Type', render: (r) => r.ticket_type || 'NA' },
    { label: 'Description', render: (r) => r.ticket_description || 'NA' },
    { label: 'Follow Up Date', render: (r) => r.follow_up_date || 'NA' }
  ]

  return (
    <dash.Container>
      {header}

      <dash.Widgets>
        <MembershipCard
          parent={parent}
          onEditBuilding={() =>
            setBuildingEdit({
              apartment:
                apartments.find((a) => a.name === parent.apartment)?.id || '',
              flat: parent.flat || ''
            })
          }
        />

        <FamilyCard childRows={children} onChildClick={setOpenChild} />

        {/* Enrolments — click to see them all. */}
        <SliderBlock
          classes={classes}
          type="enrolments"
          userInfo={userInfo}
          card={
            <Block title="enrolments">
              {enrolments.length === 0 ? (
                <NotFoundBlock message="Oh no! No enrolments found." />
              ) : (
                // The main card's Programs block: heading, then active and
                // upcoming as a TitleDescription pair in a RowFlexGap.
                <enrolStyles.Body>
                  <enrolStyles.ColumnFlexGap>
                    <enrolStyles.ColumnFlexStart>
                      <enrolStyles.Content heading type="dark">
                        Programs
                      </enrolStyles.Content>
                      <enrolStyles.RowFlexGap>
                        <TitleDescription
                          heading="active"
                          description={activeEnrolments}
                        />
                        <TitleDescription
                          heading="upcoming"
                          description={upcoming}
                        />
                      </enrolStyles.RowFlexGap>
                    </enrolStyles.ColumnFlexStart>
                  </enrolStyles.ColumnFlexGap>
                </enrolStyles.Body>
              )}
            </Block>
          }
          panel={
            <>
              <Typography variant="h6">Enrolment History</Typography>
              <DataTable
                classes={classes}
                columns={enrolmentColumns}
                rows={enrolments}
                empty="Oh no! No enrolments found."
              />
            </>
          }
        />

        {/* Monthly attendance — Attendance.js: the month's %, then the
            upcoming and last-attended session dates. */}
        <Block title="monthly attendance">
          {monthly.hasAttendees || upcomingSession ? (
            <sum.InfoBlock>
              <TitleDescription
                heading={`Attendance % (${attendance.month || ''})`}
                align="left"
                description={
                  monthly.hasAttendees
                    ? ` ${monthly.attendancePercentage} %`
                    : 'No sessions scheduled yet'
                }
              />
              <TitleDescription
                heading="upcoming session"
                description={fmtSession(upcomingSession)}
              />
              <TitleDescription
                heading="last attended session"
                description={fmtSession(attendance.lastAttendedSession)}
              />
            </sum.InfoBlock>
          ) : (
            <NotFoundBlock message="Oh no! User has no enrolments." />
          )}
        </Block>

        {/* Lifetime health — LifeTimeHealth.js: member since, sessions
            attended, attendance %. */}
        <Block title="lifetime health">
          {lifetime.hasAttendees ? (
            <healthStyles.Body>
              <healthStyles.ColumnFlexCenter>
                <TitleDescription
                  heading="Member Since"
                  size="medium"
                  align="center"
                  description={
                    attendance.memberSince
                      ? dayjs(attendance.memberSince).format('D MMM, YY')
                      : 'NA'
                  }
                />
                <healthStyles.InfoBlock>
                  <TitleDescription
                    heading="Sessions Attended"
                    size="medium"
                    description={`${lifetime.attendedCount}`}
                  />
                  <TitleDescription
                    heading="Attendance %"
                    size="medium"
                    description={`${lifetime.attendancePercentage} %`}
                  />
                </healthStyles.InfoBlock>
              </healthStyles.ColumnFlexCenter>
            </healthStyles.Body>
          ) : (
            <NotFoundBlock message="Oh no! No data to show." />
          )}
        </Block>

        {/* Payments — apartment payment links. */}
        <SliderBlock
          classes={classes}
          type="payments"
          userInfo={userInfo}
          card={
            <Block title="payments" iconType="wallet">
              <sum.InfoBlock>
                <TitleDescription
                  heading="collected"
                  size="medium"
                  description={formatAmount(paymentSummary.collected)}
                />
                <TitleDescription
                  heading="outstanding"
                  size="medium"
                  description={formatAmount(paymentSummary.outstanding)}
                />
              </sum.InfoBlock>
            </Block>
          }
          panel={
            <>
              <Typography variant="h6">Payment History</Typography>
              <DataTable
                classes={classes}
                columns={paymentColumns}
                rows={payments}
                empty="Oh no! No payments found."
              />
            </>
          }
        />

        {/* Tickets — main-system CRM, by phone number. As on the main card,
            the card itself is not clickable: each count is its own slider. */}
        <Block title="tickets log">
          <ticketStyles.Body>
            <ticketStyles.ColumnFlexCenter>
              <Slider
                width="50%"
                blockType="apartment-tickets-total"
                placeholder={
                  <div className={classes.clickable}>
                    <TitleDescription
                      heading="Total Issues Raised"
                      description={tickets.length}
                      align="center"
                    />
                  </div>
                }
                childComponent={
                  <Panel classes={classes} userInfo={userInfo}>
                    <Typography variant="h6">Tickets Log</Typography>
                    <DataTable
                      classes={classes}
                      columns={ticketColumns}
                      rows={tickets}
                      empty="Oh no! No tickets found."
                    />
                  </Panel>
                }
              />
              <Slider
                width="50%"
                blockType="apartment-tickets-open"
                placeholder={
                  <div className={classes.clickable}>
                    <TitleDescription
                      heading="Open Issues"
                      description={openTickets.length}
                      align="center"
                    />
                  </div>
                }
                childComponent={
                  <Panel classes={classes} userInfo={userInfo}>
                    <Typography variant="h6">Open Tickets</Typography>
                    <DataTable
                      classes={classes}
                      columns={ticketColumns}
                      rows={openTickets}
                      empty="Oh no! No open tickets."
                    />
                  </Panel>
                }
              />
            </ticketStyles.ColumnFlexCenter>
          </ticketStyles.Body>
        </Block>

        {/* Interactions — full width, as on the main dashboard.
            WAS A HARDCODED <NotFoundBlock/>: the card never fetched anything and
            had no click target, so it read "No interactions found" for every
            parent forever and there was no way to add one from this page. It now
            shows the latest interaction and opens the standard log drawer. */}
        <WideBlock title="interactions">
          <div
            className={classes.clickable}
            role="button"
            tabIndex={0}
            onClick={() => setLogOpen(true)}
            onKeyPress={(e) => e.key === 'Enter' && setLogOpen(true)}
          >
            <Interactions interactionsData={interactions} />
          </div>
        </WideBlock>
      </dash.Widgets>

      <Dialog
        open={Boolean(buildingEdit)}
        onClose={() => setBuildingEdit(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Apartment</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            margin="dense"
            label="Apartment"
            value={buildingEdit?.apartment || ''}
            onChange={(e) =>
              setBuildingEdit((b) => ({ ...b, apartment: e.target.value }))
            }
          >
            <MenuItem value="">
              <em>Not set</em>
            </MenuItem>
            {apartments.map((a) => (
              <MenuItem key={a.id} value={a.id}>
                {a.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            margin="dense"
            label="Flat no."
            value={buildingEdit?.flat || ''}
            onChange={(e) =>
              setBuildingEdit((b) => ({ ...b, flat: e.target.value }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBuildingEdit(null)}>Cancel</Button>
          <Button
            color="primary"
            onClick={saveBuilding}
            disabled={savingBuilding}
          >
            {savingBuilding ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <InteractionDrawer
        parent={
          logOpen && parentId
            ? { uuid: parentId, name: data?.parent?.name, phone }
            : null
        }
        onClose={() => setLogOpen(false)}
        onLogged={loadInteractions}
      />

      <ChildHistoryDrawer
        open={Boolean(openChild)}
        onClose={() => setOpenChild(null)}
        child={openChild}
      />
    </dash.Container>
  )
}

export default ApartmentFamilyDashboard
